import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  castKey,
  clearScript,
  correctLine,
  EXTRA_KINDS,
  getCast,
  getCharacters,
  getScript,
  prepareScript,
  updateScript,
  type Character,
  type ExtraKind,
  type PerformanceScript,
  type ScriptLine,
  type ScriptMark,
  type ScriptParagraph,
} from "../api";
import { useT } from "../i18n";
import CharacterFace from "../components/CharacterFace";
import ChipRow from "../components/ChipRow";
import Disclosure from "../components/Disclosure";
import MutationError from "../components/MutationError";
import {
  applyMarks,
  byteLength,
  nextPause,
  segments,
  withCue,
  withSpeaker,
  withSpeakerKind,
} from "../components/scriptMarks";

/**
 * canPrepareScript is whether "Prepare for voices" is offered for this
 * chapter: the server's `available` and nothing else — an English book on a
 * model that performs, with a voice for every character turned on (ADR-0157 §8).
 */
export function canPrepareScript(p?: PerformanceScript): boolean {
  return !!p?.available;
}

/**
 * ScriptPanel is "Prepare for voices" (ADR-0157, requirement 0090): one model
 * pass writes the chapter's performance script — a short cue before each line
 * a character speaks, pauses at story beats — and the author adjusts it here.
 *
 * Who says a line is shown with the character's face and changed with chips,
 * and that change is an attribution, written through characters (ADR-0123
 * rule 4), never the script. A cue or a pause is the script's, and is saved as
 * the paragraph's script, which the server checks changes nothing else.
 *
 * A line the roster leaves to someone else carries the script's speaker mark
 * (ADR-0157 §9): a name and an extra voice. Changing the voice of a name
 * rewrites every line of that name in the chapter and saves each paragraph, so
 * one speaker keeps one voice.
 */
export default function ScriptPanel({ bookID, chapterID }: { bookID: string; chapterID: string }) {
  const t = useT();
  const client = useQueryClient();
  const script = useQuery({
    queryKey: ["script", chapterID],
    queryFn: () => getScript(chapterID),
    retry: false,
    refetchInterval: (query) => ((query.state.data?.pending ?? 0) > 0 ? 1500 : false),
  });
  const roster = useQuery({
    queryKey: ["characters", bookID, "draft"],
    queryFn: () => getCharacters(bookID),
    enabled: canPrepareScript(script.data),
  });
  const cast = useQuery({
    queryKey: castKey(bookID),
    queryFn: () => getCast(bookID),
    enabled: canPrepareScript(script.data),
  });
  const castExtras = new Set(
    (cast.data?.roles ?? []).filter((r) => r.extra && r.voice).map((r) => r.extra as ExtraKind),
  );

  const settle = (data?: PerformanceScript) => {
    if (data) client.setQueryData(["script", chapterID], data);
    else void client.invalidateQueries({ queryKey: ["script", chapterID] });
    void client.invalidateQueries({ queryKey: ["estimate", chapterID] });
  };
  const start = useMutation({ mutationFn: () => prepareScript(chapterID), onSuccess: settle });
  const clear = useMutation({
    mutationFn: () => clearScript(chapterID),
    onSuccess: () => settle(),
  });
  const save = useMutation({
    mutationFn: (v: { paragraph: number; text: string }) =>
      updateScript(chapterID, v.paragraph, v.text),
    onSuccess: settle,
  });
  // One speaker, one voice: every paragraph naming them is saved, in turn.
  const kind = useMutation({
    mutationFn: async (v: { name: string; extra: ExtraKind }) => {
      let last: PerformanceScript | undefined;
      for (const p of script.data?.paragraphs ?? []) {
        const [marks, changed] = withSpeakerKind(p.marks, v.name, v.extra);
        if (changed) last = await updateScript(chapterID, p.index, applyMarks(p.source, marks));
      }
      return last;
    },
    onSuccess: settle,
    onError: () => settle(),
  });
  const speaker = useMutation({
    mutationFn: (v: { quote: string; choice: string }) =>
      v.choice === "someone_else" || v.choice === "not_speech"
        ? correctLine(v.quote, v.choice)
        : correctLine(v.quote, "character", v.choice),
    onSuccess: () => {
      settle();
      void client.invalidateQueries({ queryKey: ["dialogue", chapterID] });
    },
  });

  if (!canPrepareScript(script.data)) return null;
  const s = script.data!;
  const started = s.complete + s.pending + s.failed > 0;
  const busy =
    start.isPending || clear.isPending || save.isPending || speaker.isPending || kind.isPending;
  const summary = !started
    ? t.script.notStarted
    : s.pending > 0
      ? t.script.preparing(s.complete + s.failed, s.total)
      : s.failed > 0
        ? t.script.failed(s.failed)
        : t.script.summary(s.cues, s.pauses);
  const setMarks = (p: ScriptParagraph, marks: ScriptMark[]) =>
    save.mutate({ paragraph: p.index, text: applyMarks(p.source, marks) });

  return (
    <Disclosure
      testId="script-panel"
      toggleTestId="script-toggle"
      title={t.script.title}
      summary={summary}
      defaultOpen={s.pending > 0}
      className="mt-3"
    >
      <p className="mt-1 text-xs text-muted">{t.script.note}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {(!started || s.complete + s.pending < s.total || (s.unnamed > 0 && s.pending === 0)) && (
          <button
            type="button"
            data-testid="prepare-script"
            disabled={busy || s.pending > 0}
            onClick={() => start.mutate()}
            className="rounded-control border border-line px-3 py-1.5 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
          >
            {!started ? t.script.prepare : s.failed > 0 ? t.script.retry : t.script.prepareRest}
          </button>
        )}
        {started && (
          <button
            type="button"
            data-testid="clear-script"
            disabled={busy}
            onClick={() => clear.mutate()}
            className="rounded-control px-3 py-1.5 text-sm text-muted transition-colors hover:text-ink disabled:opacity-40"
          >
            {t.script.clear}
          </button>
        )}
      </div>
      {s.unnamed > 0 && s.pending === 0 && (
        <p className="mt-2 text-sm text-muted" data-testid="script-unnamed">
          {t.script.unnamed(s.unnamed)}
        </p>
      )}
      {s.pending > 0 && (
        <p className="mt-2 text-sm text-muted" data-testid="script-progress">
          {t.script.preparing(s.complete + s.failed, s.total)}
        </p>
      )}
      {s.failed > 0 && (
        <div
          role="alert"
          data-testid="script-failed"
          className="mt-2 text-sm text-red-600 dark:text-red-400"
        >
          <p>{t.script.failed(s.failed)}</p>
          <ul className="mt-1 list-disc ps-5 text-xs">
            {s.paragraphs
              .filter((p) => p.status === "failed")
              .map((p) => (
                <li key={p.index}>{p.error}</li>
              ))}
          </ul>
        </div>
      )}
      {s.paragraphs.some((p) => p.note) && (
        <div data-testid="script-notes" className="mt-2 text-sm text-ink-soft">
          <p>{t.script.notes}</p>
          <ul className="mt-1 list-disc ps-5 text-xs text-muted">
            {s.paragraphs
              .filter((p) => p.note)
              .map((p) => (
                <li key={p.index}>{p.note}</li>
              ))}
          </ul>
        </div>
      )}
      <MutationError
        className="mt-2"
        testId="script-error"
        error={start.error ?? clear.error ?? save.error ?? speaker.error ?? kind.error}
        fallback={t.script.saveFailed}
      />

      {started && (
        <div className="mt-3 max-h-[32rem] space-y-4 overflow-y-auto" data-testid="script-review">
          {s.paragraphs.map((p) => (
            <ScriptParagraphView
              key={p.index}
              paragraph={p}
              cast={roster.data?.characters ?? []}
              castExtras={castExtras}
              disabled={busy}
              onMarks={(marks) => setMarks(p, marks)}
              onSpeaker={(quote, choice) => speaker.mutate({ quote, choice })}
              onKind={(name, extra) => kind.mutate({ name, extra })}
            />
          ))}
        </div>
      )}
    </Disclosure>
  );
}

/**
 * ScriptParagraphView is one paragraph as it will be performed: the text with
 * its marks as chips, and under it each quoted line — who says it, with their
 * face, and its cue.
 */
function ScriptParagraphView({
  paragraph: p,
  cast,
  castExtras,
  disabled,
  onMarks,
  onSpeaker,
  onKind,
}: {
  paragraph: ScriptParagraph;
  cast: Character[];
  castExtras: Set<ExtraKind>;
  disabled: boolean;
  onMarks: (marks: ScriptMark[]) => void;
  onSpeaker: (quote: string, choice: string) => void;
  onKind: (name: string, extra: ExtraKind) => void;
}) {
  const t = useT();
  const end = byteLength(p.source);
  const endPause = p.marks.find(
    (m) => m.at === end && (m.kind === "pause" || m.kind === "long_pause"),
  );
  return (
    <div data-testid="script-paragraph" className="border-b border-line pb-3 last:border-0">
      {/* The authored text decides its own direction, here and nowhere inside it. */}
      <p dir="auto" className="text-sm leading-7">
        {segments(p.source, p.marks).map((seg, i) =>
          seg.kind === "text" ? (
            <span key={i}>{seg.text}</span>
          ) : seg.mark.kind === "stop" ? (
            <span key={i}>.</span>
          ) : seg.mark.kind === "speaker" ? (
            <span
              key={i}
              data-testid="script-speaker-mark"
              className="mx-0.5 rounded-full border border-sage-soft px-1.5 py-0.5 text-xs text-ink-soft"
            >
              <bdi>{seg.mark.text}</bdi>
            </span>
          ) : seg.mark.kind === "cue" ? (
            <span
              key={i}
              data-testid="script-cue"
              className="mx-0.5 rounded-full bg-sage-soft px-1.5 py-0.5 text-xs text-sage-deep"
            >
              {seg.mark.text}
            </span>
          ) : (
            <button
              key={i}
              type="button"
              data-testid="script-pause"
              title={t.script.pauseCycle}
              disabled={disabled}
              onClick={() => onMarks(nextPause(p.marks, seg.mark.at))}
              className="mx-0.5 rounded-full border border-line px-1.5 py-0.5 text-xs text-muted hover:text-ink disabled:opacity-40"
            >
              {seg.mark.kind === "long_pause" ? t.script.longPause : t.script.pause}
            </button>
          ),
        )}
      </p>
      {!endPause && (
        <button
          type="button"
          data-testid="script-add-pause"
          disabled={disabled}
          onClick={() => onMarks(nextPause(p.marks, end))}
          className="mt-1 text-xs text-muted hover:text-ink disabled:opacity-40"
        >
          + {t.script.addPause}
        </button>
      )}
      {p.lines.length > 0 && (
        <ul className="mt-2 space-y-2">
          {p.lines.map((line) => (
            <ScriptLineView
              // Starts again from the saved cue whenever the server's changes.
              key={`${line.ordinal}-${line.cue ?? ""}-${line.named ?? ""}`}
              line={line}
              cast={cast}
              castExtras={castExtras}
              disabled={disabled}
              onCue={(cue) => onMarks(withCue(p.marks, line.start, cue))}
              onSpeaker={(choice) => line.quote_id && onSpeaker(line.quote_id, choice)}
              onName={(name) =>
                onMarks(withSpeaker(p.marks, line.start, name, line.extra ?? "older_man"))
              }
              onKind={(extra) =>
                line.named
                  ? onKind(line.named, extra)
                  : onMarks(withSpeaker(p.marks, line.start, t.script.someoneElse, extra))
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ScriptLineView({
  line,
  cast,
  castExtras,
  disabled,
  onCue,
  onSpeaker,
  onName,
  onKind,
}: {
  line: ScriptLine;
  cast: Character[];
  castExtras: Set<ExtraKind>;
  disabled: boolean;
  onCue: (cue: string) => void;
  onSpeaker: (choice: string) => void;
  onName: (name: string) => void;
  onKind: (extra: ExtraKind) => void;
}) {
  const t = useT();
  const [choosing, setChoosing] = useState(false);
  const [cue, setCue] = useState(line.cue ?? "");
  const [named, setNamed] = useState(line.named ?? "");
  const who = cast.find((c) => c.id === line.character_id);
  const voiced = line.label === "character" && !!who;
  const someoneElse = line.label === "someone_else";
  const name = voiced
    ? who!.name
    : someoneElse
      ? line.named || t.script.someoneElse
      : line.label === "not_speech"
        ? t.script.notSpeech
        : t.script.unknown;
  const commit = () => {
    if (cue.trim() !== (line.cue ?? "")) onCue(cue);
  };
  const commitName = () => {
    if (named.trim() !== (line.named ?? "")) onName(named);
  };
  return (
    <li data-testid="script-line" className="flex items-start gap-2">
      <button
        type="button"
        aria-label={t.script.speakerLabel}
        aria-expanded={choosing}
        data-testid="script-speaker"
        disabled={disabled || !line.quote_id}
        onClick={() => setChoosing((open) => !open)}
        className="shrink-0 disabled:opacity-60"
      >
        <CharacterFace character={voiced ? who! : { name: t.script.narrator }} size={28} />
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-ink-soft">
          <bdi>{name}</bdi>
        </p>
        <p dir="auto" className="truncate text-xs text-muted">
          {line.text}
        </p>
        {/* A cue only where a voice may read the line: the narrator never carries one (ADR-0157 §4). */}
        {(voiced || someoneElse) && (
          <input
            type="text"
            data-testid="script-cue-input"
            aria-label={t.script.cueLabel}
            placeholder={t.script.cuePlaceholder}
            value={cue}
            disabled={disabled}
            maxLength={40}
            onChange={(e) => setCue(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            className="mt-1 w-full rounded-control border border-line bg-surface px-2 py-1 text-sm"
          />
        )}
        {/* Someone else: who it is and which extra voice reads them (ADR-0157 §9). */}
        {someoneElse && (
          <div className="mt-1" data-testid="script-extra">
            <input
              type="text"
              data-testid="script-name-input"
              aria-label={t.script.nameLabel}
              placeholder={t.script.namePlaceholder}
              value={named}
              disabled={disabled}
              maxLength={40}
              onChange={(e) => setNamed(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              className="w-full rounded-control border border-line bg-surface px-2 py-1 text-sm"
            />
            <div className="mt-1">
              <ChipRow<string>
                legend={t.script.extraLabel}
                disabled={disabled}
                value={line.extra ?? ""}
                onChange={(extra) => onKind(extra as ExtraKind)}
                options={EXTRA_KINDS.map((k) => ({
                  value: k,
                  label: t.voices.extra[k] ?? k,
                  testID: `script-extra-${k}`,
                }))}
              />
            </div>
            <p className="mt-1 text-xs text-muted">
              {line.extra && !castExtras.has(line.extra)
                ? t.script.extraUncast
                : t.script.extraNote}
            </p>
          </div>
        )}
        {choosing && (
          <div className="mt-2">
            <ChipRow<string>
              legend={t.script.speakerLabel}
              disabled={disabled}
              value={
                line.label === "character" && line.character_id
                  ? line.character_id
                  : (line.label ?? "")
              }
              onChange={(choice) => {
                setChoosing(false);
                onSpeaker(choice);
              }}
              options={[
                ...cast.map((c) => ({
                  value: c.id,
                  label: c.name,
                  testID: "script-speaker-choice",
                })),
                {
                  value: "someone_else",
                  label: t.script.someoneElse,
                  testID: "script-speaker-someone-else",
                },
                {
                  value: "not_speech",
                  label: t.script.notSpeech,
                  testID: "script-speaker-not-speech",
                },
              ]}
            />
          </div>
        )}
      </div>
    </li>
  );
}
